// /admin — operator console home: the platform "Panorama". The
// INHERTA team's at-a-glance pulse across all tenants: KPIs, system health,
// clients needing attention, account-manager load, and recent events. Data via
// the getPlatformOverview() seam → GET /admin/overview (real aggregates,
// real component health, platform worklist rows).

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { listParkedHandoffs, urgencyOf } from './handoffs-data.js'
import { getCanaryStatus, runCanaryNow } from './overview-data.js'
import {
  type AccountManagerLoad,
  type AttentionItem,
  type EventKind,
  getPlatformOverview,
  type IssueTone,
  type PlatformEvent,
  type SystemComponent,
} from './overview-data.js'
import { useSession } from '../../auth/session.js'
import {
  Banner,
  Button,
  Card,
  Chip,
  KpiTile,
  PageHeader,
  ProgressBar,
  Spinner,
} from '../../design-system/index.js'

function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Buenos días'
  if (h < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

const SYSTEM_TONE: Record<SystemComponent['status'], 'ok' | 'warn' | 'sens'> = {
  ok: 'ok',
  warn: 'warn',
  down: 'sens',
}

const ISSUE_TEXT: Record<IssueTone, string> = {
  sens: 'text-sens',
  warn: 'text-warn',
  info: 'text-info',
}

const AM_PROGRESS: Record<AccountManagerLoad['status'], 'brand' | 'warn' | 'ok'> = {
  high: 'warn',
  active: 'brand',
  away: 'ok',
}
const AM_CHIP: Record<AccountManagerLoad['status'], 'warn' | 'ok' | 'draft'> = {
  high: 'warn',
  active: 'ok',
  away: 'draft',
}

function Avatar({
  initials,
  rounded = 'full',
}: {
  initials: string
  rounded?: 'full' | 'lg'
}): JSX.Element {
  return (
    <span
      aria-hidden
      className={`grid size-8 flex-none place-items-center bg-[image:var(--brand-gradient)] text-[11px] font-bold text-white ${
        rounded === 'full' ? 'rounded-full' : 'rounded-lg'
      }`}
    >
      {initials}
    </span>
  )
}

function PanelList({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ul className="[&>li]:border-b [&>li]:border-line [&>li:last-child]:border-b-0">{children}</ul>
  )
}

function SystemRow({ s }: { s: SystemComponent }): JSX.Element {
  return (
    <li className="flex items-center gap-3 px-[18px] py-3">
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold text-text">{s.name}</span>
        <span className="mt-px block text-[11px] text-text-3">{s.detail}</span>
      </span>
      <Chip tone={SYSTEM_TONE[s.status]} dot>
        {s.statusLabel}
      </Chip>
    </li>
  )
}

function AttentionRow({ item }: { item: AttentionItem }): JSX.Element {
  const navigate = useNavigate()
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-[18px] py-3">
      <span className="flex min-w-[190px] flex-1 items-center gap-2.5">
        <Avatar initials={item.initials} rounded="lg" />
        <span className="min-w-0">
          {item.href || item.tenantId ? (
            <button
              type="button"
              onClick={() => void navigate(item.href ?? `/admin/tenants/${item.tenantId}`)}
              className="block truncate text-left text-[12.5px] font-semibold text-text hover:text-link focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
            >
              {item.name}
            </button>
          ) : (
            <span className="block truncate text-[12.5px] font-semibold text-text">
              {item.name}
            </span>
          )}
          <span className="mt-px block text-[10.5px] text-text-3">{item.meta}</span>
        </span>
      </span>
      <span className={`flex items-center gap-1.5 text-[12px] ${ISSUE_TEXT[item.issue.tone]}`}>
        <span aria-hidden className="size-1.5 flex-none rounded-full bg-current" />
        {item.issue.text}
      </span>
      <span className="ml-auto flex-none text-[11.5px] text-text-3">{item.accountManager}</span>
    </li>
  )
}

function AmRow({ am }: { am: AccountManagerLoad }): JSX.Element {
  return (
    <li className="flex items-center gap-3 px-[18px] py-3">
      <Avatar initials={am.initials} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-[12.5px] font-semibold text-text">{am.name}</span>
          <Chip tone={AM_CHIP[am.status]} dot className="ml-auto flex-none">
            {am.statusLabel}
          </Chip>
        </span>
        <span className="mt-0.5 block text-[11px] text-text-3">{am.caption}</span>
        <ProgressBar className="mt-1.5" value={am.loadPct} tone={AM_PROGRESS[am.status]} />
      </span>
    </li>
  )
}

const EVENT_ICON: Record<EventKind, { tone: string; path: string }> = {
  ok: { tone: 'bg-ok-bg text-ok', path: 'M20 6 9 17l-5-5' },
  info: { tone: 'bg-field text-text-2', path: 'M3 6h18M3 12h18M3 18h12' },
  warn: { tone: 'bg-warn-bg text-warn', path: 'M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5' },
}

function EventRow({ ev }: { ev: PlatformEvent }): JSX.Element {
  const icon = EVENT_ICON[ev.kind]
  return (
    <li className="flex gap-3 px-[18px] py-3">
      <span
        aria-hidden
        className={`grid size-7 flex-none place-items-center rounded-md ${icon.tone} [&_svg]:size-[14px]`}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={icon.path} />
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12px] leading-relaxed text-text">{ev.text}</span>
        <span className="mt-px block text-[10.5px] text-text-3">{ev.meta}</span>
      </span>
    </li>
  )
}

/**
 * Compact "who is waiting for a human" tile.
 *
 * Overview used to embed the whole parked-handoff list. That list now has its
 * own page (/admin/handoffs) with the age, the trace link and the return/keep
 * actions — so Overview shows the HEADLINE and links there, instead of two
 * implementations of the same list drifting apart.
 */
function HandoffsSummary(): JSX.Element {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'handoffs'],
    queryFn: listParkedHandoffs,
  })
  const waiting = data?.filter((h) => h.state === 'waiting_handoff') ?? []
  // The oldest UNATTENDED one is the number that matters; a conversation an
  // advisor is already holding is not a problem.
  const worst = waiting.reduce<(typeof waiting)[number] | null>(
    (acc, h) => (acc === null || h.parkedSince < acc.parkedSince ? h : acc),
    null,
  )

  return (
    <Card>
      <Card.Head
        title="Conversaciones con personas"
        sub="Quién está esperando a un asesor ahora mismo."
      />
      <Card.Body>
        {isLoading ? (
          <Spinner label="Cargando…" />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[26px] font-extrabold tabular-nums leading-none text-text">
              {waiting.length}
            </span>
            <span className="min-w-0 flex-1 text-[12.5px] text-text-2">
              {waiting.length === 0
                ? 'Nadie está esperando. Todo lo que está en manos de una persona ya tiene asesor.'
                : `sin que nadie las tome${worst ? `, la más antigua de ${worst.tenant.name}` : ''}.`}
            </span>
            {waiting.length > 0 && worst && (
              <Chip tone={urgencyOf(worst)} dot>
                Requiere atención
              </Chip>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="flex-none"
              onClick={() => void navigate('/admin/handoffs')}
            >
              Ver todas
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

/**
 * Meta send canary — the one check that proves we can still reach WhatsApp.
 *
 * The result used to live only in the worker's log, and the hosting log window
 * is about two minutes deep, so "is sending working?" was in practice
 * unanswerable. This reads the durable audit record and can trigger a check on
 * demand, which is the only way to get an answer without waiting for the
 * nightly tick.
 */
function CanaryCard(): JSX.Element {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'canary'],
    queryFn: getCanaryStatus,
  })

  async function runNow(): Promise<void> {
    setBusy(true)
    setNote('')
    const before = data?.checkedAt ?? null
    try {
      await runCanaryNow()
      // The worker does the send, so poll for a NEW result rather than assuming
      // one. Bounded: if nothing lands we say so instead of spinning forever.
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 2000))
        const next = await getCanaryStatus()
        if (next.checkedAt !== before) {
          qc.setQueryData(['admin', 'canary'], next)
          setNote('')
          setBusy(false)
          return
        }
      }
      setNote(
        'La prueba se encoló pero no llegó un resultado. Revisa que el worker tenga configuradas las credenciales del canary.',
      )
    } catch {
      setNote('No se pudo lanzar la prueba.')
    } finally {
      setBusy(false)
    }
  }

  const status = data?.status ?? 'unknown'
  const tone = status === 'ok' ? 'ok' : status === 'failed' ? 'sens' : 'draft'
  const label =
    status === 'ok' ? 'Enviando bien' : status === 'failed' ? 'Falla al enviar' : 'Sin datos'

  return (
    <Card>
      <Card.Head
        title="Canal de WhatsApp"
        sub="Comprobación real contra la API de Meta, cada noche."
      />
      <Card.Body>
        {isLoading ? (
          <Spinner label="Cargando…" />
        ) : (
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-wrap items-center gap-3">
              <Chip tone={tone} dot>
                {label}
              </Chip>
              <span className="min-w-0 flex-1 text-[12.5px] text-text-2">
                {data?.checkedAt
                  ? `Última prueba: ${new Date(data.checkedAt).toLocaleString('es-MX', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}${data.latencyMs !== null ? ` · ${String(data.latencyMs)} ms` : ''}`
                  : 'Todavía no hay ninguna prueba registrada.'}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-none"
                disabled={busy}
                onClick={() => void runNow()}
              >
                {busy ? 'Probando…' : 'Probar ahora'}
              </Button>
            </div>
            {status === 'failed' && data?.reason && (
              <Banner tone="warn">
                Meta respondió: {data.reason}. Los clientes podrían no estar recibiendo respuestas.
              </Banner>
            )}
            {note !== '' && <Banner tone="warn">{note}</Banner>}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export function OverviewPage(): JSX.Element {
  const session = useSession()
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: getPlatformOverview,
  })

  const firstName = session?.name.split(' ')[0] ?? ''

  if (isLoading || !data) {
    return (
      <div data-testid="admin-overview" className="mx-auto w-full max-w-6xl py-10">
        <Spinner label="Cargando panorama…" />
      </div>
    )
  }

  // Reflect real component health: any component not fully "ok" (warn or down)
  // means the platform is not "todo funcionando".
  const allOperational = data.systems.every((s) => s.status === 'ok')

  return (
    <div data-testid="admin-overview" className="mx-auto w-full max-w-6xl">
      <PageHeader
        title={firstName ? `${greeting()}, ${firstName}` : greeting()}
        subtitle="Así está la plataforma hoy."
        actions={
          <Chip tone={allOperational ? 'ok' : 'sens'} dot size="md">
            {allOperational ? 'Todo funcionando' : 'Atención requerida'}
          </Chip>
        }
      />

      <div className="mt-5 grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        {data.kpis.map((k) => (
          <KpiTile
            key={k.id}
            label={k.label}
            value={k.value}
            {...(k.delta ? { delta: k.delta } : {})}
            {...(k.sparkline ? { sparkline: k.sparkline } : {})}
            sparklineTone="ok"
          />
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <HandoffsSummary />
        <CanaryCard />
      </div>

      <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.45fr,1fr]">
        <div className="flex flex-col gap-4">
          <Card>
            <Card.Head title="Salud del sistema" />
            <PanelList>
              {data.systems.map((s) => (
                <SystemRow key={s.id} s={s} />
              ))}
            </PanelList>
          </Card>

          <Card>
            <Card.Head
              title="Clientes que necesitan atención"
              action={<VerTodos to="/admin/tenants" />}
            />
            <PanelList>
              {data.attention.map((item) => (
                <AttentionRow key={item.id} item={item} />
              ))}
            </PanelList>
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <Card.Head
              title="Gerentes de cuenta"
              action={<VerTodos to="/admin/account-managers" />}
            />
            <PanelList>
              {data.accountManagers.map((am) => (
                <AmRow key={am.id} am={am} />
              ))}
            </PanelList>
          </Card>

          <Card>
            <Card.Head title="Eventos recientes" />
            <PanelList>
              {data.events.map((ev) => (
                <EventRow key={ev.id} ev={ev} />
              ))}
            </PanelList>
          </Card>
        </div>
      </div>
    </div>
  )
}

function VerTodos({ to, onClick }: { to?: string; onClick?: () => void }): JSX.Element {
  const navigate = useNavigate()
  const handle = onClick ?? (to ? () => void navigate(to) : () => undefined)
  return (
    <button
      type="button"
      onClick={handle}
      className="flex items-center gap-1 text-[11.5px] font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
    >
      Ver todos
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="size-3"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </button>
  )
}
